'use client';

import { useMemo } from 'react';
import FullWidthSection from '@/components/full-width-section';
import StickyBottomBanner from '@/components/sticky-bottom-banner';
import { unlimitedCautionData } from '@/data/recommend-class/unlimited.data';
import { UNLIMITED_CHANGE_PRICE_EFFECTIVE_DATE, isDateOver } from '@/lib/date';
import { formatKoreanCurrency } from '@/lib/utils';
import { getServerTime } from '@/services/base-api';
import { useLatestStatistic } from '@/services/query/statistics.query';
import { useStoreList } from '@/services/query/store.query';
import { StoreType } from '@/services/types/store.type';
import CommonBottomCaution from '../components/common-bottom-caution';
import CommonBranchList from '../components/common-branch-list';
import CommonDororoBanner from '../components/common-dororo-banner';
import CommonReviews from '../components/common-reviews';
import CommonTopMenuTab from '../components/common-top-menu-tab';
import UnlimitedBrandStory from './components/unlimited-brand-story';
import UnlimitedCurriculum from './components/unlimited-curriculum';
import UnlimitedPricing from './components/unlimited-pricing';
import UnlimitedTopBanner from './components/unlimited-top-banner';
import { useUnlimited2WeeksMaxDiscountAmount } from '@/services/query/pricing.query';

// 메뉴 탭 데이터
const menuTabs = [
  {
    label: '브랜드 소개',
    id: 'brand-story',
  },
  {
    label: '커리큘럼',
    id: 'curriculum',
  },
  {
    label: '요금안내',
    id: 'pricing',
  },
  {
    label: '이용후기',
    id: 'reviews',
  },
];

export default function Client() {
  const { data: statisticEntity } = useLatestStatistic();
  const { data: storeList } = useStoreList({
    page: 1,
    limit: 100,
    types: StoreType.DIRECT,
  });
  const { data: maxDiscountAmountData } = useUnlimited2WeeksMaxDiscountAmount();
  const branches = storeList?.data?.stores || [];

  const recommendPrice =
    statisticEntity?.data?.recommendPrice !== undefined &&
    statisticEntity?.data?.recommendPrice !== null
      ? formatKoreanCurrency(statisticEntity.data.recommendPrice)
      : formatKoreanCurrency(200000);

  const maxDiscountAmount = maxDiscountAmountData?.data ?? 0;

  const isUnlimitedPriceDateOver = isDateOver(
    getServerTime(),
    UNLIMITED_CHANGE_PRICE_EFFECTIVE_DATE,
  );

  const cautionData = useMemo(() => {
    if (!isUnlimitedPriceDateOver) return unlimitedCautionData;
    return {
      ...unlimitedCautionData,
      items: unlimitedCautionData.items.map((item) => ({
        ...item,
        parts: item.parts.map((part) => ({
          ...part,
          text: part.text.replace('[3개월]', '[2개월]'),
        })),
      })),
    };
  }, [isUnlimitedPriceDateOver]);

  return (
    <div>
      {/* 상단 배너 영역 */}
      <UnlimitedTopBanner />

      {/* 메뉴 탭 - fixed positioning으로 스크롤 시 상단 고정 */}
      <CommonTopMenuTab tabs={menuTabs} />

      {/* 실제 콘텐츠 영역 */}
      <FullWidthSection>
        <div className="desktop:px-0">
          {/* 여기에 실제 페이지 콘텐츠가 들어갑니다 */}

          {/** 브랜드 소개 */}
          <UnlimitedBrandStory id="brand-story" />

          {/** 커리큘럼 */}
          <UnlimitedCurriculum id="curriculum" />

          {/** 요금안내 */}
          <UnlimitedPricing id="pricing" recommendPrice={recommendPrice} />

          {/** 이용후기 */}
          <CommonReviews id="reviews" />

          {/** 도로로 배너 */}
          <CommonDororoBanner />

          {/** 지점리스트 */}
          <CommonBranchList
            branches={branches}
            addTitle="면허 취득을 시작하세요"
          />

          {/** 주의사항 */}
          <CommonBottomCaution
            className="desktop:web-w mobile-w desktop:my-[120px] desktop:mx-auto mx-5 my-20"
            data={cautionData}
          />
        </div>
      </FullWidthSection>

      {/* 하단 고정 프로모션 배너 버튼 영역 */}
      <StickyBottomBanner
        webContent={{
          subtitle: [{ text: '2주만에 면허 취득 기회가!' }],
          title: [
            { text: '최대 ' },
            {
              text:
                maxDiscountAmount > 0
                  ? `${formatKoreanCurrency(maxDiscountAmount)} 할인`
                  : '15만원 할인',
              highlight: true,
              highlightColor: '#FCDE26',
            },
            { text: ' 종료까지' },
          ],
        }}
        mobileContent={{
          title: [
            { text: '최대 ' },
            {
              text:
                maxDiscountAmount > 0
                  ? `${formatKoreanCurrency(maxDiscountAmount)} 할인`
                  : '15만원 할인',
              highlight: true,
              highlightColor: '#FCDE26',
            },
            { text: ' 종료까지' },
          ],
        }}
        button={{
          text: '상담하러 가기',
          backgroundColor: 'bg-main-orange',
          action: { type: 'modal' },
        }}
      />
    </div>
  );
}
